import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { PrimaryButton, DefaultButton } from '@bingads-webui-react/fabric-with-signal';
import { Stack } from '@fluentui/react';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-message-bar';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { STEP_STATES, WIZARD_EVENTS, getCurrentStepId, getIsFirstStep, getIsLastStep, useWizardService, matchStepState } from '@bingads-webui-react/wizard';
import { NextButton, BackButton, CancelButton } from '@bingads-webui-react/wizard-ui-library';

import { BackSummaryToast } from '../steps/summary/back-to-summary-toast';
import { SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS, SEARCH_AD_GROUP_CREATION_WIZARD_STEP_PATHS } from '../steps/step-utils';

const FOOTER_DISCLAIMERS = {
  [SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.SUMMARY]: {
    text: _TL_('Your ads will start serving once they pass the editorial review. Most reviews are completed within 48 hours.'),
    topic: 'app54454',
  },
  [SEARCH_CAMPAIGN_CREATION_WIZARD_STEP_PATHS.CREATE_AD]: {
    text: _TL_('Your ads will start serving once they pass the editorial review. Most reviews are completed within 48 hours.'),
    topic: 'app54454',
  },
  [SEARCH_AD_GROUP_CREATION_WIZARD_STEP_PATHS.CREATE_AD]: {
    text: _TL_('Your ads will start serving once they pass the editorial review. Most reviews are completed within 48 hours.'),
    topic: 'app54454',
  },
  [SEARCH_AD_GROUP_CREATION_WIZARD_STEP_PATHS.CREATE_DSA_AD]: {
    text: _TL_('Your ads will start serving once they pass the editorial review. Most reviews are completed within 48 hours.'),
    topic: 'app54454',
  },
  [SEARCH_AD_GROUP_CREATION_WIZARD_STEP_PATHS.BID_AND_TARGET]: {
    text: _TL_('Some ad group settings are using your campaign settings. To edit, adjust your campaign settings.'),
  },
  [SEARCH_AD_GROUP_CREATION_WIZARD_STEP_PATHS.BID_AND_TARGET_DSA]: {
    text: _TL_('Some ad group settings are using your campaign settings. To edit, adjust your campaign settings.'),
  },
};

const SaveAndPublishButton = () => {
  const i18n = useContext(I18nContext);
  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isPublishing: true });
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [send]);

  const buttonText = useMemo(() => i18n.getString(_TL_('Go live')), [i18n]);

  return (
    <PrimaryButton
      disabled={isBlocking}
      onClick={onNextClicked}
      className="step-save-and-publish-button"
    >
      {buttonText}
    </PrimaryButton>
  );
};

export const SaveAndExitButton = ({ buttonText, disabled }) => {
  const i18n = useContext(I18nContext);

  const { state, send } = useWizardService();
  const isBlocking = matchStepState(state, STEP_STATES.FETCHING) || matchStepState(state, STEP_STATES.LOADING) || matchStepState(state, STEP_STATES.SAVING);

  const onNextClicked = useCallback(() => {
    send(WIZARD_EVENTS.SAVE_AND_EXIT);
  }, [send]);

  return (
    <DefaultButton
      disabled={disabled || isBlocking}
      onClick={onNextClicked}
      className="step-save-and-exit-button"
    >
      {buttonText || i18n.getString(_TL_('Save & exit'))}
    </DefaultButton>
  );
};
SaveAndExitButton.propTypes = {
  buttonText: PropTypes.string,
  disabled: PropTypes.bool,
};
SaveAndExitButton.defaultProps = {
  buttonText: undefined,
  disabled: false,
};

export const CreationFooter = ({
  nextButtonText,
  nextDisabled,
  backButtonText,
  cancelButtonText,
  hideBackButton,
}) => {
  const { state } = useWizardService();
  const { campaignId } = state.context.wizardData;
  const isFirstStep = getIsFirstStep(state);
  const isLastStep = getIsLastStep(state);
  const i18n = useContext(I18nContext);

  const Disclaimer = useMemo(() => () => {
    const disclaimer = FOOTER_DISCLAIMERS[getCurrentStepId(state)];
    return disclaimer ? (
      <MessageBar className="message-bar">
        <MessageBarBody>
          <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
          {disclaimer.topic ? (
            <LearnMoreLink
              topic={disclaimer.topic}
              text={i18n.getString(disclaimer.text)}
              linkText={i18n.getString(_TL_('Learn more'))}
            />
          ) : i18n.getString(disclaimer.text)}
        </MessageBarBody>
      </MessageBar>
    ) : null;
  }, [i18n, state]);

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      <Disclaimer />
      <Stack horizontal tokens={{ childrenGap: 16 }}>
        {(!isFirstStep && !hideBackButton) && <BackButton buttonText={backButtonText} />}
        {(!isLastStep ? <NextButton buttonText={nextButtonText} disabled={nextDisabled} /> : <SaveAndPublishButton />)}
        {isFirstStep && !(campaignId && campaignId > 0) && <CancelButton buttonText={cancelButtonText} />}
        {!isFirstStep && <SaveAndExitButton />}
      </Stack>
      <BackSummaryToast />
    </Stack>
  );
};
CreationFooter.propTypes = {
  nextButtonText: PropTypes.string,
  nextDisabled: PropTypes.bool,
  backButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  hideBackButton: PropTypes.bool,
};
CreationFooter.defaultProps = {
  nextButtonText: undefined,
  nextDisabled: false,
  backButtonText: undefined,
  cancelButtonText: undefined,
  hideBackButton: false,
};
